import React, {useEffect, useState} from "react";
import styled from "styled-components";
import axios from "axios";
import FairInfoBar from "../FairInfoBar";
import { useNavigate } from "react-router-dom";
import { RedButton, WhiteButton } from "../Button";
import Heading from "../Heading";
import { convertToCAD, convertToUSD } from "../../utils/CurrencyConversion";
import { handleGenericErrors, handleGenericSuccess } from "../../utils/ErrorUtility";
import LeftRail from "./LeftRail/LeftRail";
import { trackFinFormBackButton, trackFinFormPageChange, trackFinFormSaveAndContinueButton, updateUserNetSales, updateUserTotalSales } from "../../DumbleData";

interface Props {
	language: string;
}

export const FinFormSalesSummaryPage = ( {language} : Props ) => {
    const [fairInfo, setFairInfo] = useState<any>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [cash, setCash] = useState<string>("0.00");
    const [cheques, setCheques] = useState<string>("0.00");
    const [manualTransactions, setManualTransactions] = useState<string>("0.00");
    const [wirelessTransactions, setWirelessTransactions] = useState<string>("0.00");
    const [totalSales, setTotalSales] = useState<string>("0.00");
    const [taxes, setTaxes] = useState<string>("0.00");
    const [netSales, setNetSales] = useState<string>("0.00");
    const [latestStep, setLatestStep] = useState<string>("");
    const navigate = useNavigate();

    const handleBack = async () => {
        trackFinFormBackButton();
        navigate("/financial-form/sales");
    }

    // put request
    const handleSalesSummary = async () => {
        trackFinFormSaveAndContinueButton();
        try {
            await axios.put(
                `${process.env.REACT_APP_ENV_BASE_URL}/api/user/fairs/current/financials/form/sales-summary`,
                    {
                        withCredentials: true,
                    }
            )
            .then((response) => {
                if (response.status === 200) {
                    navigate("/financial-form/rewards");
                }
                else {
                    handleGenericSuccess(response);
                }
            })
            .catch((err) => {
                handleGenericErrors(err);
            });
        } catch (err: any) {
            handleGenericErrors(err);
        }
    };

    // get request
    useEffect(() => {
        const getFinancialFormSalesSummary = async () => {
            try {
                await axios.get(
                    `${process.env.REACT_APP_ENV_BASE_URL}/api/user/fairs/current/financials/form/sales-summary`,
                        {
                            withCredentials: true,
                        }
                )
                .then((response) => {
                    if (response.status === 200) {
                        if (!response.data.isFinancialFormAvailable) {
                            navigate("/financial-form/intro");
                        };
                        if (response.data.latestStep === "CLOSED") {
                            navigate("/financial-form/confirmation");
                        }
                        if (response.data.latestStep === "SALES") {
                            navigate("/financial-form/intro");
                        }

                        const fairInfo = response.data.fair;
                        fairInfo.start = response.data.fair.startDate;
                        fairInfo.end = response.data.fair.endDate;
                        setFairInfo(fairInfo);
                        setLatestStep(response.data.latestStep)

                        const sales = response.data.sales;
                        setCash(sales.cash);
                        setCheques(sales.cheques);
                        setManualTransactions(sales.manualTransactions);
                        setWirelessTransactions(sales.wirelessTransactions);
                        setTotalSales(response.data.totalSales);
                        setTaxes(response.data.taxes);
                        setNetSales(response.data.netSales);

                        updateUserTotalSales(response.data.totalSales);
                        updateUserNetSales(response.data.netSales);
                    }
                    else {
                        handleGenericSuccess(response);
                    }
                })
                .catch((err) => {
                    handleGenericErrors(err);
                });
            } catch (err: any) {
                handleGenericErrors(err);
            } finally {
                setIsLoading(false);
                trackFinFormPageChange("Financial Form Sales Summary Page");
            }
        };
        getFinancialFormSalesSummary();
    }, [navigate]);

    // handling left rail clicks
    // this function is very simple because no changes can be made on this page
    const handleNextLocation = (nextLocation: string) => {
        navigate(nextLocation);
    }

    return (<>
        {!isLoading && (<>
            <FairInfoBar
                language={language}
                enableSwitch={fairInfo.enableSwitch}
                fairInfoData={fairInfo}
                organizationData={fairInfo}
                pageType={"financial-form"}
                />
            <LeftRail
                language={language}
                latestStep={latestStep}
                handleNextLocation={handleNextLocation}
            />
            <StyledDiv>
                <StyledHeading>{language === "en" ? "Sales Summary" : "Sommaire des ventes"}</StyledHeading>
                <StyledCard>
                    <SubHeading>
                        {language === "en" ? "Commission Worksheet" : "Informations relatives aux commandes dans le "}
                        <br />
                        {language === "en" ? "Sales Summary" : "formulaire de récompenses"}
                    </SubHeading>
                    <StyledRow>
                        <StyledLabel>
                            <StyledSubLabel>{language === "en" ? "Interac/Debit and Credit" : "Débit/Interac et crédit"}</StyledSubLabel>
                            {language === "en" ? "(Total the amounts for all credit cards and Interac from ALL End of Day Reports)" : "(Faites le total des montants de toutes les cartes de crédit et de débit/Interac de TOUS les rapports de fin de journée.)"}
                            <br/>
                        </StyledLabel>
                        <StyledNumber>{language === "en" ? "$" + convertToUSD(wirelessTransactions) : convertToCAD(wirelessTransactions) + " $"}</StyledNumber>
                    </StyledRow>
                    <StyledRow>
                        <p>{language === "en" ? "Cash" : "Argent comptant"}</p>
                        <p>+{language === "en" ? "$" + convertToUSD(cash) : convertToCAD(cash) + " $"}</p>
                    </StyledRow>
                    <StyledRow>
                        <p>{language === "en" ? "Cheques" : "Chèques"}</p>
                        <p>+{language === "en" ? "$" + convertToUSD(cheques) : convertToCAD(cheques) + " $"}</p>
                    </StyledRow>
                    <StyledRow>
                        <StyledLabel>
                            <StyledSubLabel>{language === "en" ? "Manual Imprinter Transactions " : "Transactions manuelles par imprimante"}</StyledSubLabel>
                            {language === "en" ? "(If applicable)" : "(le cas échéant)"}
                        </StyledLabel>
                        <StyledNumber>+{language === "en" ? "$" + convertToUSD(manualTransactions) : convertToCAD(manualTransactions) + " $"}</StyledNumber>
                    </StyledRow>
                    <StyledRow className="totalSales">
                        <p>{language === "en" ? "Total Sales" : "Total des ventes"}</p>
                        <p>{language === "en" ? "$" + convertToUSD(totalSales) : convertToCAD(totalSales) + " $"}</p>
                    </StyledRow>
                    <StyledRow>
                        <p>{language === "en" ? "Applicable Taxes" : "Taxes applicables"}</p>
                        <p>-{language === "en" ? "$" + convertToUSD(taxes) : convertToCAD(taxes) + " $"}</p>
                    </StyledRow>
                    <StyledRow className="netSales">
                        <p>{language === "en" ? "Net Sales" : "Ventes nettes"}</p>
                        <p>{language === "en" ? "$" + convertToUSD(netSales) : convertToCAD(netSales) + " $"}</p>
                    </StyledRow>
                </StyledCard>
                <StyledButtons>
                    <StyledWhiteButton
                        handleClick={handleBack}
                    >
                        {language === "en" ? "BACK" : "Retour"}
                    </StyledWhiteButton>
                    <StyledRedButton
                        handleClick={handleSalesSummary}
                    >
                        {language === "en" ? "SAVE & CONTINUE" : "Sauvegarder et continuer"}
                    </StyledRedButton>
                </StyledButtons>
            </StyledDiv>
        </>)}
    </>);
};

export default FinFormSalesSummaryPage;

const StyledLabel = styled.label`
    display: block;
	font-family: museo-sans;
	font-style: normal;
	font-weight: normal;
	font-size: 13px;
	line-height: 15px;
	color: #333333;
	padding-bottom: 8px;
    padding-right: 16px;
	&.virtual {
		padding: 0px;
	}
`;

const StyledSubLabel = styled.label`
	display: block;
	font-family: museo-sans;
	font-style: normal;
	font-size: 16px;
    font-weight: 500;
    line-height: 19px;
	color: #333333;
	padding-bottom: 8px;
`;

const StyledRow = styled.div`
    border-top: 1px solid #D7D7D7;
    padding: 12px 0px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    font-family: museo-sans;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 19px;
    &.totalSales {
        border-top: 2px solid #333;
        font-weight: 700;
    }
    &.netSales {
        font-weight: 700;
    }
`;

const StyledDiv = styled.div`
    text-align: right;
    max-width: 600px;
    margin: 0px auto;
    padding: 40px 40px 120px 40px;
    font-family: museo-sans;
    font-size: 39px;
    font-weight: 300;
    line-height: 42px;
    height: auto;
    @media (max-width: 1080px) {
        padding-top: 90px;
    }
`;

const StyledNumber = styled.p`
    margin: auto 0px;
    text-wrap-mode: nowrap;
`;

const StyledHeading = styled(Heading)`
    text-align: left;
    max-width: 600px;
    margin: 0px auto;
    padding: 30px 0px;
    font-family: museo-sans
    font-size: 39px;
    font-weight: 300;
    line-height: 42px;
`;

const SubHeading = styled(Heading)`
    text-align: left;
    font-family: museo-sans;
    font-size: 28px;
    font-weight: 300;
    line-height: 32px;
    text-align: left;
    padding-bottom: 24px;
`;

const StyledCard = styled.div`
    text-align: left;
    max-width: 600px;
    background-color: #F5F5F5;
    margin: 0px auto;
    padding: 48px 80px;
    box-sizing: border-box;
    border-radius: 16px;
    @media (max-width: 519px) {
        padding: 48px 25px;
    }
`;

const StyledButtons = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    margin-top: 26px;
`;

const buttonStyles = `
    height: 40px;
    margin-top: 6px;
    font-family: museo-sans;
    font-size: 11px;
    font-weight: 700;
    line-height: 12px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    width: 100%;
    max-width: 178px;
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 15px;
`;

export const StyledRedButton = styled(RedButton)`
    ${buttonStyles}
`;

export const StyledWhiteButton = styled(WhiteButton)`
    ${buttonStyles}
    border: 1px solid #E81111;
    background: #FFF;
`;